import React, { useContext, useEffect, useState } from 'react'
import styled from 'styled-components'
import {
  TextField,
  Select,
  MenuItem,
  InputLabel,
  Typography,
  Radio,
  RadioGroup,
  FormControl,
  FormLabel,
  FormControlLabel,
  Button,
  CircularProgress,
} from '@mui/material'
import { DatePicker } from '@mui/lab'
import { ThemeContext } from 'styled-components'
import { useTranslation } from 'react-i18next'
import { useFormik } from 'formik'
import AdapterDateFns from '@mui/lab/AdapterDateFns'
import LocalizationProvider from '@mui/lab/LocalizationProvider'
import { array, object, func } from 'prop-types'
import moment from 'moment'
import usePrevious from '../hooks/usePrevious'
import ReCAPTCHA from 'react-google-recaptcha'
import { allowMockApi, hiveApiUrl, reCaptchaSiteKey } from '../firebase'
import { useHistory } from 'react-router'

const FormContainer = styled.form`
  padding: 1rem 0;
`

const FormRow = styled.div`
  display: flex;

  @media (max-width: 680px) {
    flex-direction: column;
  }
`

const FieldContainer = styled.div`
  width: 17rem;
  margin: 0.5rem 0;

  :first-of-type {
    margin-right: 2rem;
  }
`

const RadioWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
`

const CAPTCHAContainer = styled.div`
  margin-top: 1rem;
`

function AppointmentForm({ appointmentTypes, datePickerProps, reqParams }) {
  const themeContext = useContext(ThemeContext)
  const { t, i18n } = useTranslation()
  const history = useHistory()

  const [captchaPassed, setCaptchaPassed] = useState(!reCaptchaSiteKey)
  const [submitError, setSubmitError] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const onSubmit = ({
    fullName,
    email,
    company,
    phone,
    appointmentType,
    appointmentDate,
    availableTime,
  }) => {
    const startTime =
      String(moment(appointmentDate).format('YYYY-MM-DD')) + 'T' + String(availableTime) + ':00'

    const body = {
      data: {
        resourceId: reqParams.resource,
        fullName,
        email,
        company,
        phone,
        appointmentTypeId: appointmentType,
        startTime,
      },
    }

    const url = allowMockApi
      ? `/mock/create/${reqParams.company}`
      : `${hiveApiUrl}/api/appointment/create?company=${reqParams.company}`

    setSubmitting(true)

    fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body),
    })
      .then((res) => res.json())
      .then((json) => {
        setSubmitting(false)
        history.push(
          `/appointment?company=${reqParams.company}&resource=${reqParams.resource}&booking=${json.data.bookingId}`,
        )
      })
      .catch((error) => setSubmitError(error))

    !!reCaptchaSiteKey && setCaptchaPassed(false)
  }

  const initialValues = {
    fullName: reqParams.name || '',
    email: reqParams.email || '',
    company: '',
    phone: '',
    appointmentType: (appointmentTypes.length === 1 && appointmentTypes[0][0]) || '',
    appointmentDate: reqParams.date || null,
    availableTime: '',
  }

  const formik = useFormik({
    onSubmit,
    initialValues,
  })

  const commonProps = {
    variant: 'standard',
    fullWidth: true,
    onChange: formik.handleChange,
  }

  const typeAndDateSelected = formik.values.appointmentType && formik.values.appointmentDate

  const [selectedType, setSelectedType] = useState(null)
  const previousSelectedType = usePrevious(selectedType)

  const [selectedDate, setSelectedDate] = useState(null)
  const previousSelectedDate = usePrevious(selectedDate)

  const [availableTimes, setAvailableTimes] = useState([])

  const durationSelected = formik.values.appointmentType
    ? appointmentTypes.filter((type) => type[0] === formik.values.appointmentType)[0][1]
        .appointmentLength
    : null

  const getEndTime = (startTime) =>
    moment(startTime, 'HH:mm').add(durationSelected, 'minutes').format('HH:mm')

  const utcAppointmentDate = moment(formik.values.appointmentDate).format('YYYY-MM-DD')

  useEffect(() => {
    if (
      typeAndDateSelected &&
      (selectedType !== previousSelectedType || selectedDate !== previousSelectedDate)
    ) {
      // eslint-disable-next-line no-undef
      const url = allowMockApi
        ? `/mock/resource-availability/${reqParams.company}/${reqParams.resource}/${utcAppointmentDate}/${durationSelected}/${formik.values.appointmentType}`
        : `${hiveApiUrl}/api/appointment/available?company=${reqParams.company}&resourceId=${
            reqParams.resource
          }&startTime=${utcAppointmentDate}&duration=${durationSelected}&appointmentTypeId=${
            formik.values.appointmentType
          }&timestamp=${new Date().getTime()}`

      fetch(url)
        .then((res) => res.json())
        .then((json) => {
          const data = json.data.startTimes
          setAvailableTimes(data.length > 0 ? data : [{ error: true }])
        })
    }
  }, [
    typeAndDateSelected,
    selectedType,
    previousSelectedType,
    selectedDate,
    previousSelectedDate,
    durationSelected,
  ])

  return (
    <FormContainer onSubmit={formik.handleSubmit}>
      <Typography
        variant="h6"
        component="h6"
        style={{ color: `${themeContext.palette.primary.main}` }}
      >
        {t('makeAnAppointment')}
      </Typography>
      <FormRow>
        <FieldContainer>
          <InputLabel id="appointment-type-label">{t('appointmentType')}</InputLabel>
          <Select
            variant="standard"
            fullWidth
            onChange={(newValue) => {
              formik.handleChange(newValue)
              setSelectedType(newValue.target.value)
            }}
            name="appointmentType"
            id="appointmentType"
            value={formik.values.appointmentType}
            labelId="appointment-type-label"
            label={t('appointmentType')}
            required
          >
            {appointmentTypes.map((type) => (
              <MenuItem value={type[0]} key={type[0]}>
                {type[1].displayName[i18n.language]} ({type[1].appointmentLength} minutes)
              </MenuItem>
            ))}
          </Select>
        </FieldContainer>
      </FormRow>
      <Typography
        variant="subtitle1"
        component="h6"
        style={{ marginTop: '1.5rem', color: `${themeContext.palette.primary.main}` }}
      >
        {t('selectDesiredTime')}
      </Typography>
      <FormRow>
        <FieldContainer>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              name="appointmentDate"
              id="appointmentDate"
              value={formik.values.appointmentDate}
              onChange={(newValue) => {
                formik.setFieldValue('appointmentDate', newValue)
                setSelectedDate(moment(newValue).format('YYYY-MM-DD'))
              }}
              renderInput={(params) => (
                // (Ilya) - onKeyDown is to prevent user input
                // of dates other than those available in the picker popup
                <TextField {...params} onKeyDown={(e) => e.preventDefault()} />
              )}
              labelId="appointment-date-label"
              label={t('appointmentDate')}
              required
              {...datePickerProps}
            />
          </LocalizationProvider>
        </FieldContainer>
      </FormRow>
      <FormRow>
        <FieldContainer style={{ width: '20rem' }}>
          <FormControl component="fieldset">
            <FormLabel component="legend">{t('availableTime')}</FormLabel>
            <RadioGroup
              name="availableTime"
              id="availableTime"
              value={formik.values.availableTime}
              onChange={formik.handleChange}
              aria-label="available-time"
              required
            >
              <RadioWrapper>
                {availableTimes.length > 0 ? (
                  availableTimes.map((time, index) => {
                    if (time.error) {
                      return (
                        <Typography
                          key={index}
                          variant="body2"
                          color="error"
                          component="p"
                          style={{ marginTop: '1rem' }}
                        >
                          {t('selectDifferentDate')}
                        </Typography>
                      )
                    }

                    return (
                      <FormControlLabel
                        key={time}
                        value={time}
                        control={<Radio />}
                        label={`${time} - ${getEndTime(time)}`}
                      />
                    )
                  })
                ) : (
                  <Typography variant="body2" component="p" style={{ marginTop: '1rem' }}>
                    {t('selectTypeAndDate')}
                  </Typography>
                )}
              </RadioWrapper>
            </RadioGroup>
          </FormControl>
        </FieldContainer>
      </FormRow>
      <Typography
        variant="subtitle1"
        component="h6"
        style={{ marginTop: '1.5rem', color: `${themeContext.palette.primary.main}` }}
      >
        {t('customerInfo')}
      </Typography>
      <FormRow>
        <FieldContainer>
          <TextField
            name="fullName"
            id="fullName"
            value={formik.values.fullName}
            label={t('fullName')}
            required
            {...commonProps}
          />
        </FieldContainer>
        <FieldContainer>
          <TextField
            name="email"
            type="email"
            id="email"
            value={formik.values.email}
            label={t('email')}
            required
            {...commonProps}
          />
        </FieldContainer>
      </FormRow>
      <FormRow>
        <FieldContainer>
          <TextField
            name="company"
            id="company"
            value={formik.values.company}
            label={t('company')}
            {...commonProps}
          />
        </FieldContainer>
        <FieldContainer>
          <TextField
            name="phone"
            id="phone"
            value={formik.values.phone}
            label={t('phone')}
            required
            {...commonProps}
          />
        </FieldContainer>
      </FormRow>
      <CAPTCHAContainer>
        {!!reCaptchaSiteKey && (
          <ReCAPTCHA sitekey={reCaptchaSiteKey} onChange={() => setCaptchaPassed(true)} />
        )}
      </CAPTCHAContainer>
      <Button variant="contained" type="submit" disabled={!captchaPassed}>
        {submitting ? <CircularProgress color="secondary" size={20} /> : t('submit')}
      </Button>
      {submitError !== '' && (
        <Typography variant="body2" color="error" component="p" style={{ marginTop: '0.5rem' }}>
          Please try again - {String(submitError)}
          <Button variant="text" onClick={() => history.go(0)}>
            {t('refreshPage')}
          </Button>
        </Typography>
      )}
    </FormContainer>
  )
}

AppointmentForm.propTypes = {
  appointmentTypes: array,
  datePickerProps: object,
  filterExceptedDates: func,
  reqParams: object,
}

export default AppointmentForm
